import { CLIENT_ERROR_LIMITS, type ClientErrorReport } from '@pm/shared';

/**
 * The rules for reporting failures that happen inside the browser, apart from
 * the browser so they can be tested: which pages are in scope, what a report
 * carries, and how many are sent.
 *
 * Only what the server cannot see is reported - a render crash, an unhandled
 * rejection, a window error, a request that never got an answer. An HTTP
 * error that did get an answer is not: the server already logged it, with
 * more detail than the page has.
 */

/** A render loop can throw thousands of times a second; the tenth copy teaches nothing the first did not. */
export const MAX_REPORTS_PER_PAGE_LOAD = 20;
export const DEDUPE_WINDOW_MS = 60_000;

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const BOARD = new RegExp(`^/boards/(${UUID})`, 'i');
const WORKSPACE = new RegExp(`^/workspaces/(${UUID})`, 'i');

/** PM's pages, and the board or workspace one is showing - what makes a report actionable. */
export function pmContext(pathname: string): { isPm: boolean; boardId?: string; workspaceId?: string } {
  const boardId = BOARD.exec(pathname)?.[1];
  const workspaceId = WORKSPACE.exec(pathname)?.[1];
  const isPm = pathname === '/' || Boolean(boardId || workspaceId)
    || ['/my-tasks', '/my-day', '/automations', '/reports'].some((p) => pathname === p || pathname.startsWith(`${p}/`));
  return { isPm, ...(boardId ? { boardId } : {}), ...(workspaceId ? { workspaceId } : {}) };
}

const cut = (value: string | undefined, max: number) =>
  value === undefined ? undefined : value.length > max ? value.slice(0, max) : value;

/** Trims a report to what the API accepts, so an oversized stack is shortened rather than refused. */
export function boundReport(report: ClientErrorReport): ClientErrorReport {
  return {
    ...report,
    message: cut(report.message, CLIENT_ERROR_LIMITS.message)!,
    stack: cut(report.stack, CLIENT_ERROR_LIMITS.stack),
    component: cut(report.component, CLIENT_ERROR_LIMITS.component),
    url: cut(report.url, CLIENT_ERROR_LIMITS.url),
    requestUrl: cut(report.requestUrl, CLIENT_ERROR_LIMITS.requestUrl),
  };
}

/**
 * Decides which reports go out: at most `max` per page load, and the same
 * kind and message at most once per `dedupeMs`. `send` is only called for
 * those; it must never throw back into whatever just failed.
 */
export function createReporter({
  send, now = Date.now, max = MAX_REPORTS_PER_PAGE_LOAD, dedupeMs = DEDUPE_WINDOW_MS,
}: {
  send: (report: ClientErrorReport) => void;
  now?: () => number;
  max?: number;
  dedupeMs?: number;
}) {
  let sent = 0;
  const lastSent = new Map<string, number>();

  return (report: ClientErrorReport): boolean => {
    const bounded = boundReport(report);
    if (!bounded.message.trim() || sent >= max) return false;

    const key = `${bounded.kind}|${bounded.message}`;
    const at = now();
    const previous = lastSent.get(key);
    if (previous !== undefined && at - previous < dedupeMs) return false;

    lastSent.set(key, at);
    sent += 1;
    send(bounded);
    return true;
  };
}
